<script lang="ts">
  /**
   * Player - The letter character controlled by the player
   * Displays a large styled letter that the player moves around
   */

  import { scale } from 'svelte/transition';

  // Props
  interface Props {
    letter: string;
    x: number;
    y: number;
    size?: number;
    showPulse?: boolean;
  }

  let {
    letter,
    x,
    y,
    size = 80,
    showPulse = false
  }: Props = $props();

  // Generate a color based on the letter for variety
  function getLetterColor(letter: string): string {
    const colors = [
      '#EF4444', // Red
      '#F97316', // Orange
      '#FBBF24', // Yellow
      '#22C55E', // Green
      '#3B82F6', // Blue
      '#A855F7', // Purple
    ];
    const index = letter.charCodeAt(0) % colors.length;
    return colors[index];
  }

  $effect(() => {
    // Reactive effect for letter color
    letterColor = getLetterColor(letter);
  });

  let letterColor = $state(getLetterColor(letter));
</script>

<div
  class="player"
  class:pulse={showPulse}
  style="
    left: {x}px;
    top: {y}px;
    width: {size}px;
    height: {size}px;
    --letter-color: {letterColor};
  "
  role="img"
  aria-label="Player character letter {letter}"
>
  <span class="letter">{letter}</span>
</div>

<style>
  .player {
    position: absolute;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: white;
    border: 4px solid var(--letter-color);
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.3),
      0 0 20px var(--letter-color, rgba(124, 58, 237, 0.4));
    transition: transform 0.25s ease-out;
    z-index: 10;
    animation: breathe 2s ease-in-out infinite;
  }

  .letter {
    font-size: 48px;
    font-weight: bold;
    color: var(--letter-color);
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    user-select: none;
  }

  /* Breathing animation for idle state */
  @keyframes breathe {
    0%, 100% {
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      transform: translate(-50%, -50%) scale(1.05);
    }
  }

  /* Pulse animation for correct hits */
  .pulse {
    animation: pulseHit 0.3s ease-out, breathe 2s ease-in-out infinite;
  }

  @keyframes pulseHit {
    0% {
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      transform: translate(-50%, -50%) scale(1.3);
    }
    100% {
      transform: translate(-50%, -50%) scale(1);
    }
  }

  /* Responsive sizing */
  @media (max-width: 767px) {
    .player {
      border-width: 3px;
    }

    .letter {
      font-size: 40px;
    }
  }
</style>
